function differenceInDays(dateLeft: Date, dateRight: Date) {
  const diffInMs = dateLeft.getTime() - dateRight.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  // Calculate months (approximate)
  const months = Math.floor(diffInDays / 30.44);

  // Calculate years (approximate)
  const years = Math.floor(diffInDays / 365.25);

  return {
    days: diffInDays,
    hours: diffInHours,
    minutes: diffInMinutes,
    seconds: diffInSeconds,
    months,
    years,
  };
}

export function relativeTime(date: string | Date | null) {
  if (!date) {
    return 'N/A';
  }

  date = date instanceof Date ? date : new Date(date);

  const daysFromNow = differenceInDays(new Date(), date);

  if (daysFromNow.days < 0) {
    return `${Math.abs(daysFromNow.days)} days from now`;
  }

  if (daysFromNow.days === 1) {
    return `${daysFromNow.days} day ago`;
  }

  if (daysFromNow.days > 0) {
    return `${daysFromNow.days} days ago`;
  }

  if (daysFromNow.days === 0) {
    if (daysFromNow.hours === 1) {
      return `${daysFromNow.hours} hour ago`;
    }

    if (daysFromNow.hours > 1) {
      return `${daysFromNow.hours} hours ago`;
    }

    if (daysFromNow.minutes > 0) {
      return daysFromNow.minutes === 1 ? `${daysFromNow.minutes} minute ago` : `${daysFromNow.minutes} minutes ago`;
    }

    return 'Just now';
  }
}

export function relativeTimeShort(date: string | Date | null) {
  if (!date) {
    return 'n/a';
  }

  date = date instanceof Date ? date : new Date(date);
  const diff = differenceInDays(new Date(), date);

  if (diff.years > 0) {
    return `${diff.years}y`;
  }

  if (diff.months > 0) {
    return `${diff.months}mo`;
  }

  if (diff.days > 0) {
    // Convert days to weeks if more than 7 days
    if (diff.days >= 7) {
      const weeks = Math.floor(diff.days / 7);
      return `${weeks}w`;
    }
    return `${diff.days}d`;
  }

  if (diff.hours > 0) {
    return `${diff.hours}h`;
  }

  if (diff.minutes > 0) {
    return `${diff.minutes}m`;
  }

  return 'now';
}

/**
 * Converts seconds to the best time unit representation
 * @param seconds - The duration in seconds
 * @returns Object with value and unit
 */
export function secondsToDuration(seconds: number | null | undefined) {
  if (!seconds || seconds === 0) {
    return null;
  }

  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(seconds / 3600);
  const days = Math.floor(seconds / 86400);

  // Return the largest whole unit that makes sense
  if (days >= 1) {
    return { value: days, unit: 'days' };
  }

  if (hours >= 1) {
    return { value: hours, unit: 'hours' };
  }

  if (minutes >= 1) {
    return { value: minutes, unit: 'minutes' };
  }

  return { value: seconds, unit: 'seconds' };
}

/**
 * Formats a duration in seconds to a human-readable string
 * @param seconds - The duration in seconds
 * @returns Formatted string like "15 minutes", "2 hours", "1 day"
 */
export function formatDuration(seconds: number | null | undefined) {
  const duration = secondsToDuration(seconds);

  if (!duration) {
    return null;
  }

  const unit =
    duration.value === 1
      ? duration.unit.slice(0, -1) // Remove 's' for singular
      : duration.unit;

  return `${duration.value} ${unit}`;
}

/**
 * Converts time value and unit to seconds
 * @param value - The numeric value
 * @param unit - The unit ('minutes', 'hours', 'days', 'seconds')
 * @returns Duration in seconds
 */
export function durationToSeconds(
  value: number | null | undefined,
  unit: 'minutes' | 'hours' | 'days' | 'seconds' = 'minutes',
) {
  if (!value) {
    return 0;
  }

  const multiplier = {
    seconds: 1,
    minutes: 60,
    hours: 3600,
    days: 86400,
  };

  return value * multiplier[unit];
}
